'use client'

// React Imports
import { useMemo, useState } from 'react'

// MUI Imports
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Skeleton from '@mui/material/Skeleton'
import { useTheme, alpha } from '@mui/material/styles'

// Recharts Imports
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from '@/libs/Recharts'

// Types
import type { RevenueVsExpensesChartProps, MonthlyRevenueExpensesData } from '@/types/dashboards/resumeTypes'

// Hooks
import { useDashboardResumeTranslations, useTranslations } from '@/hooks/useTranslations'

const RevenueVsExpensesChart = ({ data, loading = false }: RevenueVsExpensesChartProps) => {
  const { t } = useDashboardResumeTranslations()
  const { t: commonT } = useTranslations()
  const theme = useTheme()
  const [period, setPeriod] = useState<'6m' | '12m' | '24m'>('12m')

  // Encontrar o índice do mês atual nos dados da API.
  // A API retorna 24 meses (12 passados + 12 futuros), o mês atual estará no meio.
  const currentMonthIndex = useMemo(() => {
    if (!data || data.length === 0) return 11

    return Math.floor(data.length / 2) - 1
  }, [data])

  // Mapear dados para incluir tradução dos meses. Memoizado — recharts itera por
  // referência no chartDataContext, e nova referência a cada render reinicia o
  // ciclo de dispatch e dispara `Maximum update depth exceeded`.
  const chartData = useMemo(() => {
    return (data || []).map(item => ({
      ...item,
      month: commonT(`months.${item.month}`) || item.month
    }))
  }, [data, commonT])

  // Componente de tooltip customizado
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      // Determinar se é um mês futuro (previsto) baseado no índice do mês atual
      const itemIndex = payload[0]?.payload?.originalIndex ?? payload[0]?.payload?.monthIndex ?? -1
      const isForecast = parseInt(itemIndex) > currentMonthIndex
      const currentYear = new Date().getFullYear()
      const yearSuffix = String(currentYear).slice(-2) // Últimos 2 dígitos do ano

      return (
        <Card sx={{ p: 2, boxShadow: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {label} - {yearSuffix}
            {isForecast && (
              <Typography component="span" variant="caption" sx={{ ml: 1, fontStyle: 'italic', color: 'text.secondary' }}>
                ({t('revenueChart.forecast')})
              </Typography>
            )}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  bgcolor: entry.color
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {entry.name}:
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(entry.value)}
              </Typography>
            </Box>
          ))}
        </Card>
      )
    }

    return null
  }

  // Filtrar dados baseado no período selecionado, centralizando no mês atual.
  const filteredData = useMemo(() => {
    if (!chartData || chartData.length === 0) return []

    const totalMonths = chartData.length
    const current = data ? currentMonthIndex : Math.floor(totalMonths / 2) - 1

    if (period === '24m') return chartData

    if (period === '12m') {
      const start = Math.max(0, current - 5)
      const end = Math.min(totalMonths, current + 7)

      return chartData.slice(start, end)
    }

    if (period === '6m') {
      const start = Math.max(0, current - 2)
      const end = Math.min(totalMonths, current + 4)

      return chartData.slice(start, end)
    }

    return chartData
  }, [chartData, currentMonthIndex, data, period])

  const handlePeriodChange = (
    event: React.MouseEvent<HTMLElement>,
    newPeriod: '6m' | '12m' | '24m'
  ) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod)
    }
  }

  if (loading) {
    return (
      <Card sx={{ height: '100%', minHeight: 350 }}>
        <CardHeader
          title={<Skeleton variant="text" width={200} height={28} />}
          action={<Skeleton variant="rectangular" width={150} height={32} />}
        />
        <CardContent>
          <Skeleton variant="rectangular" width="100%" height={300} />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card sx={{ height: '100%', minHeight: 350 }}>
      <CardHeader
        title={
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {t('charts.revenueVsExpenses')}
          </Typography>
        }
        action={
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            sx={{ height: 32 }}
          >
            <ToggleButton value="6m" sx={{ px: 2 }}>
              6M
            </ToggleButton>
            <ToggleButton value="12m" sx={{ px: 2 }}>
              12M
            </ToggleButton>
            <ToggleButton value="24m" sx={{ px: 2 }}>
              24M
            </ToggleButton>
          </ToggleButtonGroup>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 0 }}>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart
            data={filteredData}
            margin={{
              top: 20,
              right: 30,
              left: 40,
              bottom: 20
            }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--mui-palette-divider)"
              vertical={false}
            />
            <XAxis
              dataKey="month"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'var(--mui-palette-text-disabled)' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: 'var(--mui-palette-text-disabled)' }}
              tickFormatter={(value) =>
                `${(value / 1000000).toFixed(1)}M`
              }
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="top"
              height={36}
              iconType="circle"
              wrapperStyle={{
                paddingBottom: '20px',
                fontSize: '14px',
                color: theme.palette.text.primary
              }}
            />
            {/* Receitas Recebidas - verde sólido */}
            <Line
              type="monotone"
              dataKey="revenueReceived"
              stroke={theme.palette.success.main}
              strokeWidth={3}
              dot={{ fill: theme.palette.success.main, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: theme.palette.success.main, strokeWidth: 2 }}
              name={t('revenueChart.revenueReceived')}
            />

            {/* Receitas a Receber - verde claro tracejado */}
            <Line
              type="monotone"
              dataKey="revenueReceivable"
              stroke={alpha(theme.palette.success.main, 0.4)}
              strokeWidth={2}
              strokeDasharray="6 3"
              dot={{ fill: alpha(theme.palette.success.main, 0.4), strokeWidth: 1, r: 3 }}
              activeDot={{ r: 5, stroke: alpha(theme.palette.success.main, 0.6), strokeWidth: 2 }}
              name={t('revenueChart.revenueReceivable')}
            />

            {/* Despesas Pagas - vermelho sólido */}
            <Line
              type="monotone"
              dataKey="expensesPaid"
              stroke={theme.palette.error.main}
              strokeWidth={3}
              dot={{ fill: theme.palette.error.main, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: theme.palette.error.main, strokeWidth: 2 }}
              name={t('revenueChart.expensesPaid')}
            />

            {/* Despesas a Pagar - vermelho claro tracejado */}
            <Line
              type="monotone"
              dataKey="expensesPayable"
              stroke={alpha(theme.palette.error.main, 0.4)}
              strokeWidth={2}
              strokeDasharray="6 3"
              dot={{ fill: alpha(theme.palette.error.main, 0.4), strokeWidth: 1, r: 3 }}
              activeDot={{ r: 5, stroke: alpha(theme.palette.error.main, 0.6), strokeWidth: 2 }}
              name={t('revenueChart.expensesPayable')}
            />

            {/* Lucro Líquido - azul sólido */}
            <Line
              type="monotone"
              dataKey="netProfit"
              stroke={theme.palette.info.main}
              strokeWidth={3}
              dot={{ fill: theme.palette.info.main, strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6, stroke: theme.palette.info.main, strokeWidth: 2 }}
              name={t('revenueChart.netProfit')}
            />

            {/* Lucro Estimado - azul claro tracejado */}
            <Line
              type="monotone"
              dataKey="estimatedProfit"
              stroke={alpha(theme.palette.info.main, 0.4)}
              strokeWidth={2}
              strokeDasharray="6 3"
              dot={{ fill: alpha(theme.palette.info.main, 0.4), strokeWidth: 1, r: 3 }}
              activeDot={{ r: 5, stroke: alpha(theme.palette.info.main, 0.6), strokeWidth: 2 }}
              name={t('revenueChart.estimatedProfit')}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

export default RevenueVsExpensesChart
